import React, { useState, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import InternationalPhoneInput from './common/InternationalPhoneInput';
import './SignupForm.scss';

interface SignupFormProps {
  onSuccess?: () => void;
  redirectTo?: string;
}

const SignupForm: React.FC<SignupFormProps> = ({ onSuccess, redirectTo }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    dateOfBirth: '',
    agreeToTerms: false,
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const { signup } = useAuth();
  const navigate = useNavigate();
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
  const googleRedirectUri = process.env.REACT_APP_GOOGLE_REDIRECT_URI || '';

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    // First name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    // Last name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Username validation
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 6) {
      newErrors.username = 'Username must be at least 6 characters';
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain letters, numbers, and underscores';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      newErrors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Phone number validation (optional but if provided, should be valid)
    if (formData.phoneNumber && !/^\+?[\d\s\-()]+$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }

    // Terms agreement validation
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const result = await signup({
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        username: formData.username,
        password: formData.password,
        phone_number: formData.phoneNumber || undefined,
        date_of_birth: formData.dateOfBirth || undefined,
      });
      
      if (result.success) {
        if (onSuccess) {
          onSuccess();
        } else if (redirectTo) {
          navigate(redirectTo);
        } else {
          navigate('/');
        }
      } else {
        setSubmitError(result.message || 'Signup failed. Please try again.');
      }
    } catch (error) {
      setSubmitError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const googleAuthUrl = useMemo(() => {
    if (!googleClientId || !googleRedirectUri) {
      return '';
    }

    const url = new URL('https://accounts.google.com/o/oauth2/v2/auth');
    url.searchParams.set('client_id', googleClientId);
    url.searchParams.set('redirect_uri', googleRedirectUri);
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('scope', 'openid email profile');
    url.searchParams.set('access_type', 'online');
    url.searchParams.set('prompt', 'select_account');
    if (redirectTo && redirectTo.startsWith('/')) {
      url.searchParams.set('state', redirectTo);
    }
    return url.toString();
  }, [googleClientId, googleRedirectUri, redirectTo]);

  const handleGoogleRedirect = useCallback(() => {
    if (!googleAuthUrl) {
      setSubmitError('Google signup is not configured. Please try again later.');
      return;
    }
    window.location.href = googleAuthUrl;
  }, [googleAuthUrl]);

  return (
    <div className="signup-form-container">
      <div className="signup-form-card">
        <div className="signup-form-header">
          <h2 className="signup-form-title">Create Account</h2>
          <p className="signup-form-subtitle">Join us and start your journey</p>
        </div>

        <Form onSubmit={handleSubmit} className="signup-form">
          {submitError && (
            <Alert variant="danger" className="mb-3">
              {submitError}
            </Alert>
          )}

          <div className="row">
            <div className="col-md-6">
          <Form.Group className="mb-3">
            <Form.Label htmlFor="firstName" className="text-left">
              <i className="fas fa-user me-2"></i>
              First Name <span className="text-danger">*</span>
            </Form.Label>
                <Form.Control
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  isInvalid={!!errors.firstName}
                  placeholder="Enter your first name"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.firstName}
                </Form.Control.Feedback>
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label htmlFor="lastName" className="text-left">
                  <i className="fas fa-user me-2"></i>
                  Last Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  isInvalid={!!errors.lastName}
                  placeholder="Enter your last name"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.lastName}
                </Form.Control.Feedback>
              </Form.Group>
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label htmlFor="email" className="text-left">
              <i className="fas fa-envelope me-2"></i>
              Email Address <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              isInvalid={!!errors.email}
              placeholder="Enter your email address"
            />
            <Form.Control.Feedback type="invalid">
              {errors.email}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label htmlFor="username" className="text-left">
              <i className="fas fa-at me-2"></i>
              Username <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              id="username"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              isInvalid={!!errors.username}
              placeholder="Choose a username"
            />
            <Form.Control.Feedback type="invalid">
              {errors.username}
            </Form.Control.Feedback>
          </Form.Group>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label htmlFor="password" className="text-left">
                  <i className="fas fa-lock me-2"></i>
                  Password <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  isInvalid={!!errors.password}
                  placeholder="Create a password"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.password}
                </Form.Control.Feedback>
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label htmlFor="confirmPassword" className="text-left">
                  <i className="fas fa-lock me-2"></i>
                  Confirm Password <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  isInvalid={!!errors.confirmPassword}
                  placeholder="Confirm your password"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.confirmPassword}
                </Form.Control.Feedback>
              </Form.Group>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <InternationalPhoneInput
                  value={formData.phoneNumber}
                  onChange={(value) => setFormData(prev => ({ ...prev, phoneNumber: value || '' }))}
                  placeholder="Enter your phone number"
                  id="phoneNumber"
                  name="phoneNumber"
                  error={errors.phoneNumber}
                  label="Phone Number (Optional)"
                  showLabel={true}
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label htmlFor="dateOfBirth">
                  <i className="fas fa-calendar me-2"></i>
                  Date of Birth (Optional)
                </Form.Label>
                <Form.Control
                  type="date"
                  id="dateOfBirth"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              id="agreeToTerms"
              name="agreeToTerms"
              checked={formData.agreeToTerms}
              onChange={handleInputChange}
              isInvalid={!!errors.agreeToTerms}
              label={
                <span>
                  I agree to the{' '}
                  <Link to="/terms" className="text-decoration-none">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="text-decoration-none">
                    Privacy Policy
                  </Link>
                </span>
              }
            />
            {errors.agreeToTerms && (
              <div className="text-danger small mt-1">{errors.agreeToTerms}</div>
            )}
          </Form.Group>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-100 signup-submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creating Account...
              </>
            ) : (
              <>
                <i className="fas fa-user-plus me-2"></i>
                Create Account
              </>
            )}
          </Button>
        </Form>

        <div className="signup-form-footer">
          <p className="text-center text-muted mb-0">
            Already have an account?{' '}
            <Link to="/login" className="text-decoration-none fw-bold">
              Sign in here
            </Link>
          </p>
        </div>

        <div className="signup-form-divider">
          <span>or</span>
        </div>

        <div className="social-signup">
          <Button
            type="button"
            variant="outline-primary"
            size="lg"
            className="w-100"
            onClick={handleGoogleRedirect}
            disabled={isSubmitting || !googleAuthUrl}
          >
            <i className="fab fa-google me-2"></i>
            Continue with Google
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SignupForm;
